import React, { useRef } from "react";
import { SearchCode, SearchCodeType } from "@/shared/config/globals";

interface Props {
  onClick: (code: SearchCodeType) => void;
  selectedCode?: SearchCodeType;
  onFocusOutRight?: () => void;
}

// 50音表のグリッドレイアウト (5列固定)。null は空セル。
// 行単位で記述することで、カーソルキーでの上下左右移動を直感的に扱える。
type Cell = { key: string; value: SearchCodeType } | null;
const c = (k: keyof typeof SearchCode): Cell => ({ key: k, value: SearchCode[k] });
const GRID: Cell[][] = [
  [c("a"),  c("i"),  c("u"),  c("e"),  c("o")],
  [c("ka"), c("ki"), c("ku"), c("ke"), c("ko")],
  [c("sa"), c("si"), c("su"), c("se"), c("so")],
  [c("ta"), c("ti"), c("tu"), c("te"), c("to")],
  [c("na"), c("ni"), c("nu"), c("ne"), c("no")],
  [c("ha"), c("hi"), c("hu"), c("he"), c("ho")],
  [c("ma"), c("mi"), c("mu"), c("me"), c("mo")],
  [c("ya"), null,    c("yu"), null,    c("yo")],
  [c("ra"), c("ri"), c("ru"), c("re"), c("ro")],
  [c("wa"), null,    null,    null,    null  ],
];
const COLS = 5;

export const SearchCodeButtons = ({ onClick, selectedCode, onFocusOutRight }: Props) => {
  const inputRefs = useRef<Record<string, HTMLInputElement | null>>({});

  // 指定セルにフォーカス。空セルの場合は移動方向に沿って最寄りの有効セルを探す。
  const focusCell = (row: number, col: number, dRow: number, dCol: number) => {
    let r = row;
    let cc = col;
    for (let i = 0; i < GRID.length * COLS; i++) {
      if (r < 0 || r >= GRID.length || cc < 0 || cc >= COLS) return;
      const cell = GRID[r][cc];
      if (cell) {
        inputRefs.current[cell.key]?.focus();
        return;
      }
      r += dRow;
      cc += dCol;
    }
  };

  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
    row: number,
    col: number,
    value: SearchCodeType,
  ) => {
    switch (e.key) {
      case "Enter":
        e.preventDefault();
        onClick(value);
        return;
      case "ArrowLeft":
        e.preventDefault();
        focusCell(row, col - 1, 0, -1);
        return;
      case "ArrowRight":
        e.preventDefault();
        // 右隣に有効セルが無ければ金融機関リストへフォーカスを抜ける
        // (右端列: オコソトノホモヨロ、およびワ行のワ)
        {
          let nextCol = col + 1;
          while (nextCol < COLS && !GRID[row][nextCol]) nextCol++;
          if (nextCol >= COLS) {
            onFocusOutRight?.();
          } else {
            focusCell(row, nextCol, 0, 1);
          }
        }
        return;
      case "ArrowUp":
        e.preventDefault();
        focusCell(row - 1, col, -1, 0);
        return;
      case "ArrowDown":
        e.preventDefault();
        focusCell(row + 1, col, 1, 0);
        return;
    }
  };

  return (
    <ol>
      {GRID.flatMap((rowCells, row) =>
        rowCells.map((cell, col) => {
          if (!cell) {
            return <li key={`empty-${row}-${col}`}>  </li>;
          }
          const { key, value } = cell;
          return (
            <li key={key}>
              <input
                ref={(el) => {
                  inputRefs.current[key] = el;
                }}
                type="checkbox"
                id={`item-${key}`}
                checked={selectedCode === value}
                onChange={() => onClick(value)}
                onKeyDown={(e) => handleKeyDown(e, row, col, value)}
              />
              <label htmlFor={`item-${key}`}>{value}</label>
            </li>
          );
        }),
      )}
    </ol>
  );
};
